/**
 * Which part of this frame the person can actually see.
 *
 * Embedded in a Rumio site, the app's iframe is sized to its content (the
 * `rumio:height` message), so the frame itself never scrolls: the host page
 * does. `position: fixed` then means "fixed inside the frame", and anything
 * placed at the top of the frame is off-screen once the host page has scrolled
 * past it. The host sends no scroll position, and a cross-origin frame cannot
 * read it. What a frame *can* ask is how much of it intersects the top-level
 * viewport: an IntersectionObserver with no root measures against the window
 * of the top document, even across origins, and reports the visible rectangle
 * in the frame's own coordinates.
 *
 * `useVisibleArea()` returns that rectangle (top and height within the frame)
 * while embedded and the browser supports it, else null (the viewport is the
 * frame, and the usual `fixed` placement is right). Thresholds every 2% keep it
 * current while the host page scrolls.
 */

import { useEffect, useState } from "react";
import { inIframe } from "./iframe-bridge";

export interface VisibleArea {
  /** Distance from the frame's top edge to the first visible pixel. */
  top: number;
  /** Height of the visible part. */
  height: number;
}

const THRESHOLDS = Array.from({ length: 51 }, (_, i) => i / 50);

let current: VisibleArea | null = null;
const listeners = new Set<(a: VisibleArea | null) => void>();
let observer: IntersectionObserver | null = null;

function start() {
  if (observer || typeof window === "undefined" || typeof IntersectionObserver === "undefined" || !inIframe()) return;
  observer = new IntersectionObserver(
    (entries) => {
      const e = entries[entries.length - 1];
      if (!e) return;
      const r = e.intersectionRect;
      // intersectionRect is in the frame's viewport coordinates; the frame does not scroll,
      // so these are document coordinates too. Nothing visible: keep the last known area.
      if (r.height <= 0) return;
      current = { top: Math.max(0, r.top - e.boundingClientRect.top), height: r.height };
      for (const l of listeners) l(current);
    },
    { threshold: THRESHOLDS },
  );
  observer.observe(document.documentElement);
}

export function useVisibleArea(): VisibleArea | null {
  const [area, setArea] = useState<VisibleArea | null>(current);
  useEffect(() => {
    start();
    listeners.add(setArea);
    setArea(current);
    return () => {
      listeners.delete(setArea);
    };
  }, []);
  return area;
}

/**
 * Measure again now. Callbacks come only when the visible fraction crosses a threshold, so
 * a reading taken while the host page was still settling can outlive the layout it described
 * (seen on lettrova.com: 288 px believed visible of a 639 px frame wholly in view). Observing
 * afresh always delivers a current reading.
 */
export function remeasureVisibleArea() {
  if (!observer) return;
  observer.unobserve(document.documentElement);
  observer.observe(document.documentElement);
}

/** The last measured area without subscribing; for one-off placement. */
export function visibleArea(): VisibleArea | null {
  start();
  return current;
}
